/**
 * Custom hook for managing A/B comparison sessions
 */
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from '../i18n/LanguageContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function useCompare() {
  const { t, language } = useTranslation();
  const [compareStage, setCompareStage] = useState('setup'); // setup, running-a, running-b, generating, report
  const [activeSession, setActiveSession] = useState(null); // 'A' or 'B'

  // Framing configs
  const [framingA, setFramingA] = useState({ topic: '', description: '', questions: [''], moderator_briefing: '' });
  const [framingB, setFramingB] = useState({ topic: '', description: '', questions: [''], moderator_briefing: '' });

  // Session data (keyed by 'A' or 'B')
  const [sessionsData, setSessionsData] = useState({
    A: { messages: [], roundSummaries: [], finalAnalysis: null, moderatorSummary: '', beliefStates: {}, sessionId: null },
    B: { messages: [], roundSummaries: [], finalAnalysis: null, moderatorSummary: '', beliefStates: {}, sessionId: null },
  });

  const [comparisonAnalysis, setComparisonAnalysis] = useState(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState(null);

  // Refs for streaming
  const streamingContentRef = useRef('');
  const currentSpeakerRef = useRef(null);
  const activeSessionRef = useRef(null);

  const updateFramingA = useCallback((field, value) => {
    setFramingA(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateFramingB = useCallback((field, value) => {
    setFramingB(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleStreamEvent = useCallback((event) => {
    const session = event.session || activeSessionRef.current;
    if (!session) return;

    switch (event.type) {
      case 'compare_session_start':
        activeSessionRef.current = event.session;
        setActiveSession(event.session);
        setCompareStage(event.session === 'A' ? 'running-a' : 'running-b');
        setSessionsData(prev => ({
          ...prev,
          [event.session]: { ...prev[event.session], sessionId: event.session_id },
        }));
        break;

      case 'round_start':
        setCurrentRound(event.round);
        break;

      case 'speaker_start':
        currentSpeakerRef.current = {
          name: event.speaker,
          id: event.speaker_id,
          round: event.round,
          model: event.model,
        };
        streamingContentRef.current = '';
        setCurrentSpeaker(currentSpeakerRef.current);
        setStreamingContent('');
        break;

      case 'content_chunk':
        streamingContentRef.current += event.chunk;
        setStreamingContent(streamingContentRef.current);
        break;

      case 'speaker_end': {
        const finalContent = streamingContentRef.current;
        const speakerInfo = currentSpeakerRef.current;

        if (finalContent && finalContent.trim()) {
          const newMessage = {
            id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            speaker: speakerInfo?.name || event.speaker,
            speakerId: speakerInfo?.id || 'unknown',
            content: finalContent,
            round: speakerInfo?.round || 0,
            model: speakerInfo?.model,
          };
          setSessionsData(prev => ({
            ...prev,
            [session]: {
              ...prev[session],
              messages: [...prev[session].messages, newMessage],
            },
          }));
        }

        streamingContentRef.current = '';
        currentSpeakerRef.current = null;
        setStreamingContent('');
        setCurrentSpeaker(null);
        break;
      }

      case 'message_analyzed':
        setSessionsData(prev => {
          const msgs = prev[session].messages;
          if (msgs.length === 0) return prev;
          const updated = [...msgs];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            sentiment: event.sentiment,
            keyPoints: event.key_points,
          };
          return { ...prev, [session]: { ...prev[session], messages: updated } };
        });
        break;

      case 'belief_update':
        setSessionsData(prev => ({
          ...prev,
          [session]: {
            ...prev[session],
            beliefStates: {
              ...prev[session].beliefStates,
              [event.speaker_id]: [...(prev[session].beliefStates[event.speaker_id] || []), event.belief_state],
            },
          },
        }));
        break;

      case 'round_summary':
        setSessionsData(prev => ({
          ...prev,
          [session]: {
            ...prev[session],
            roundSummaries: [...prev[session].roundSummaries, event.summary],
          },
        }));
        break;

      case 'moderator_summary_chunk':
        setSessionsData(prev => ({
          ...prev,
          [session]: {
            ...prev[session],
            moderatorSummary: prev[session].moderatorSummary + event.chunk,
          },
        }));
        break;

      case 'final_analysis':
        setSessionsData(prev => ({
          ...prev,
          [session]: { ...prev[session], finalAnalysis: event.analysis },
        }));
        break;

      case 'generating_comparison':
        setCompareStage('generating');
        break;

      case 'comparison_analysis':
        setComparisonAnalysis(event.analysis);
        break;

      case 'compare_complete':
        setCompareStage('report');
        break;

      case 'error':
        setError(event.message);
        break;

      default:
        break;
    }
  }, []);

  const startComparison = useCallback(async (sharedConfig, personas) => {
    if (!framingA.topic || !framingB.topic || personas.length === 0) return;

    setCompareStage('running-a');
    setError(null);
    setComparisonAnalysis(null);
    setSessionsData({
      A: { messages: [], roundSummaries: [], finalAnalysis: null, moderatorSummary: '', beliefStates: {}, sessionId: null },
      B: { messages: [], roundSummaries: [], finalAnalysis: null, moderatorSummary: '', beliefStates: {}, sessionId: null },
    });

    const cleanQuestions = (qs) => (qs || ['']).filter(q => q.trim());

    try {
      const response = await fetch(`${API_BASE}/sessions/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: sharedConfig.title || '',
          framing_a: {
            topic: framingA.topic,
            description: framingA.description || '',
            questions: cleanQuestions(framingA.questions),
            moderator_briefing: framingA.moderator_briefing || '',
          },
          framing_b: {
            topic: framingB.topic,
            description: framingB.description || '',
            questions: cleanQuestions(framingB.questions),
            moderator_briefing: framingB.moderator_briefing || '',
          },
          personas: personas,
          rounds: sharedConfig.rounds || 5,
          moderator_model: sharedConfig.moderatorModel || 'claude-sonnet-4.6',
          language: language,
          devils_advocate: sharedConfig.devilsAdvocate || false,
          enhanced_belief_tracking: sharedConfig.enhancedBeliefTracking || false,
        }),
      });

      if (!response.ok) throw new Error('Failed to start comparison');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              handleStreamEvent(event);
            } catch (e) {
              console.error('Failed to parse compare event:', e);
            }
          }
        }
      }
    } catch (err) {
      setError(t('errors.sessionError'));
      console.error(err);
      setCompareStage('setup');
    }
  }, [framingA, framingB, handleStreamEvent, language, t]);

  const resetComparison = useCallback(() => {
    setCompareStage('setup');
    setActiveSession(null);
    setFramingA({ topic: '', description: '', questions: [''], moderator_briefing: '' });
    setFramingB({ topic: '', description: '', questions: [''], moderator_briefing: '' });
    setSessionsData({
      A: { messages: [], roundSummaries: [], finalAnalysis: null, moderatorSummary: '', beliefStates: {}, sessionId: null },
      B: { messages: [], roundSummaries: [], finalAnalysis: null, moderatorSummary: '', beliefStates: {}, sessionId: null },
    });
    setComparisonAnalysis(null);
    setCurrentRound(0);
    setCurrentSpeaker(null);
    setStreamingContent('');
    setError(null);
  }, []);

  return {
    compareStage,
    activeSession,
    framingA,
    framingB,
    sessionsData,
    comparisonAnalysis,
    currentRound,
    currentSpeaker,
    streamingContent,
    error,
    setError,

    updateFramingA,
    updateFramingB,
    setFramingA,
    setFramingB,
    startComparison,
    resetComparison,
  };
}
